import { useEffect, useState, useRef, useCallback, useLayoutEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'src/utils/i18n'
import Image from 'next/image'
import useScrollTrigger from '@mui/material/useScrollTrigger'
import { getUserAccountDevice, getUserAccountOrder, getServerTime } from 'src/@api/login.js'
import { extendSubscriptionTime } from '/src/@api/dashboard'
import countryAndIp from 'src/lib/getCountryAndIp'
import { HEADER_HIDE_SCROLL_OPTIONS } from 'src/@core/layouts/components/vertical/appBar'
import { refreshSubscriptionExpire, reloadAfterRenewSuccess } from 'src/utils/refreshSubscriptionExpire'
import {
  buildMsafelyPurchaseHref,
  buildMsafelyRenewCheckoutHref,
  fetchRenewProductIdForOrder
} from 'src/utils/renewalPlans'
import { resolveDeviceOrder } from 'src/utils/currentOrder'
import { resolveUpgradeDeviceId } from 'src/utils/resolveUpgradeDeviceId'
import useRenewalPlans from 'src/hooks/useRenewalPlans'
import { navigateTo } from 'src/utils/navigate'

const currencyMap = [
  { country: 'IT', currency: 'EUR' },
  { country: 'FR', currency: 'EUR' },
  { country: 'ES', currency: 'EUR' },
  { country: 'BE', currency: 'EUR' },
  { country: 'DE', currency: 'EUR' },
  { country: 'NL', currency: 'EUR' },
  { country: 'PT', currency: 'EUR' },
  { country: 'HR', currency: 'EUR' },
  { country: 'GR', currency: 'EUR' },
  { country: 'AT', currency: 'EUR' },
  { country: 'BR', currency: 'BRL' },
  { country: 'AU', currency: 'AUD' },
  { country: 'CA', currency: 'CAD' },
  { country: 'KR', currency: 'KRW' },
  { country: 'GB', currency: 'GBP' },
  { country: 'IN', currency: 'INR' },
  { country: 'JP', currency: 'JPY' },
  { country: 'TR', currency: 'TRY' },
  { country: 'ZA', currency: 'ZAR' },
  { country: 'RO', currency: 'RON' },
  { country: 'CH', currency: 'CHF' },
  { country: 'MX', currency: 'MXN' },
  { country: 'MY', currency: 'MYR' },
  { country: 'SG', currency: 'SGD' },
  { country: 'BG', currency: 'BGN' },
  { country: 'IL', currency: 'ILS' },
  { country: 'PL', currency: 'PLN' },
  { country: 'NO', currency: 'NOK' },
  { country: 'SE', currency: 'SEK' },
  { country: 'HU', currency: 'HUF' },
  { country: 'CZ', currency: 'CZK' },
  { country: 'AR', currency: 'ARS' },
  { country: 'TH', currency: 'THB' },
  { country: 'NZ', currency: 'NZD' }
]

const fallbackRates = {
  AUD: 1.4193,
  BGN: 5.2484,
  BRL: 5.283,
  CAD: 1.3624,
  CHF: 0.78017,
  CNY: 6.9011,
  CZK: 20.998,
  DKK: 6.4311,
  EUR: 0.86073,
  GBP: 0.74841,
  HKD: 7.8209,
  HUF: 333.58,
  IDR: 16909,
  ILS: 3.0734,
  INR: 91.65,
  ISK: 124.55,
  JPY: 157.54,
  KRW: 1475.79,
  MXN: 17.6338,
  MYR: 3.943,
  NOK: 9.6523,
  NZD: 1.689,
  PHP: 58.625,
  PLN: 3.6775,
  RON: 4.384,
  SEK: 9.1999,
  SGD: 1.2772,
  THB: 31.665,
  TRY: 43.994,
  ZAR: 16.5028
}

const readDeviceUser = () => {
  try {
    return JSON.parse(localStorage.getItem('DeviceUser') || '{}') || {}
  } catch {
    return {}
  }
}

const getSelectedDeviceId = () => {
  const deviceUser = readDeviceUser()
  const id =
    deviceUser.device_id != null && deviceUser.device_id !== '' ? deviceUser.device_id : deviceUser.id

  return id != null && id !== '' ? id : null
}

/**
 * 定位当前设备：先按 device_id / id，再按 iCloud 账号兜底。
 * 兜底是必要的：部分写入 DeviceUser 的路径（绑定流程等）不带 device_id，
 * 只按 id 匹配会拿不到设备 → 续费弹窗拿不到当前订单（套餐与价格全空）。
 */
const resolveCurrentDevice = (deviceList = []) => {
  const list = Array.isArray(deviceList) ? deviceList : []
  if (!list.length) return null

  const selectedId = getSelectedDeviceId()
  if (selectedId != null) {
    const byId = list.find(
      item => String(item.id) === String(selectedId) || String(item.device_id) === String(selectedId)
    )
    if (byId) return byId
  }

  const deviceUser = readDeviceUser()
  const account = deviceUser.username || deviceUser.iCloud_username
  if (account) {
    return list.find(item => item.iCloud_username === account || item.username === account) || null
  }

  return null
}

const ExpirationReminder = ({ orders, paymentFallbackOnly = false }) => {
  const { t, locale } = useTranslation('common')
  const dispatch = useDispatch()
  const [isDisplay, setIsDisplay] = useState(false)
  const [isExpired, setIsExpired] = useState(false)
  const [remainingDays, setRemainingDays] = useState(0)
  const [expirationDays, setExpirationDays] = useState(0)
  const [serverDate, setServerDate] = useState('')

  // 控制弹窗在“将到期”与“已过期”两种状态下的文案
  const [isExpiredPopup, setIsExpiredPopup] = useState(false)
  const [isDisplay2, setIsDisplay2] = useState(false)
  const [order, setOrder] = useState({})
  const username = JSON.parse(localStorage.getItem('DeviceUser'))?.username || ''

  const [isDisplayPayment, setIsDisplayPayment] = useState(false)
  const [checkID, setCheckID] = useState(0)
  const [isDisplayPlan, setIsDisplayPlan] = useState(false)
  const [deviceId, setDeviceId] = useState(null)
  const deviceUserKey = useSelector(state => state.Common.deviceUser)

  const { plans, planIndex, setPlanIndex, selectedPlan } = useRenewalPlans({
    orderId: order?.id,
    expireTime: order?.expire_time,
    productName: order?.product_name,
    enabled: order?.id != null && order?.id !== ''
  })

  const applyOrderRow = useCallback((deviceOrId, orderList) => {
    if (!orderList?.length) return

    const device = deviceOrId && typeof deviceOrId === 'object' ? deviceOrId : null
    const deviceUser = readDeviceUser()
    const id = device ? device.id : deviceOrId

    // 与 Dashboard 卡片同一套匹配：order_id → device_id（多张取最晚过期）→ iCloud 账号
    const row = resolveDeviceOrder({ orders: orderList, deviceUser, device, deviceId: id })

    if (row) {
      setOrder(row)
      setCheckID(row?.bill_card ? 0 : 1)
    }
  }, [])

  // 用户已在弹窗里选好套餐 → 直达官网支付页（与 my account 的「添加卡」一致，见 71f5416），
  // 少一步「再选一次套餐」；拿不到套餐编号时退回官网套餐页兜底，避免点了没反应。
  // 必须带上当前设备的「设备包 id」，否则官网建单会另开一个设备包（多绑定一个 package）。
  const getRenewCheckoutHref = () => {
    const deviceId = resolveUpgradeDeviceId(deviceUserKey)

    return (
      buildMsafelyRenewCheckoutHref({ locale, productId: selectedPlan?.productId, deviceId }) ||
      buildMsafelyPurchaseHref({ locale, deviceId })
    )
  }

  const [isDisplayResult, setIsDisplayResult] = useState(false)
  const [paymentResult, setPaymentResult] = useState('success')
  const [errorType, setErrorType] = useState('')

  const updateExpirationFromServer = useCallback(
    (expireTimeRaw, serverTimeRaw) => {
      const expireTime = expireTimeRaw == null || expireTimeRaw === '' ? NaN : new Date(expireTimeRaw).getTime()
      const numericServerTime = Number(serverTimeRaw)
      const parsedServerTime = Number.isFinite(numericServerTime)
        ? numericServerTime < 1e12
          ? numericServerTime * 1000
          : numericServerTime
        : new Date(serverTimeRaw).getTime()

      // Use server time when available; a server-time outage should not hide the reminder.
      const nowTime = Number.isFinite(parsedServerTime) && parsedServerTime > 0 ? parsedServerTime : Date.now()

      if (!Number.isFinite(expireTime) || expireTime <= 0) {
        setIsExpired(false)
        setIsDisplay(false)
        setIsDisplay2(false)
        setRemainingDays(0)
        setExpirationDays(0)
        setServerDate('')

        return
      }

      const nowDate = new Date(nowTime).toISOString().slice(0, 10)
      const closeDate = localStorage.getItem(`closeExpirationReminder-${username}`)
      setServerDate(nowDate)

      if (expireTime > nowTime) {
        setIsExpired(false)
        const remaining = Math.ceil((expireTime - nowTime) / (1000 * 60 * 60 * 24))
        setIsDisplay(remaining <= 7 && nowDate !== closeDate)
        setRemainingDays(remaining)

        if (remaining <= 3) {
          const key = `DisplayExpiredPopup-${username}`
          const savedDate = localStorage.getItem(key)
          if (savedDate === nowDate) {
            setIsDisplay2(false)
          } else {
            setIsExpiredPopup(false)
            setIsDisplay2(true)
            localStorage.setItem(key, nowDate)
          }
        } else {
          setIsDisplay2(false)
        }
      } else {
        setIsExpired(true)
        setIsDisplay(true)
        setExpirationDays(Math.ceil((nowTime - expireTime) / (1000 * 60 * 60 * 24)))
        setIsDisplay2(false)
      }
    },
    [username]
  )

  useEffect(() => {
    const init = async () => {
      const [res, serverTimeRes] = await Promise.all([
        getUserAccountDevice({ page: 1, pageSize: 10 }).catch(() => null),
        getServerTime().catch(() => null)
      ])
      if (res?.code === 0) {
        const device = resolveCurrentDevice(res.data)
        const deviceUser = JSON.parse(localStorage.getItem('DeviceUser') || '{}')

        // 解析到期时间：优先 DeviceUser，无效再回退接口 device；都无效则不展示提醒（避免「0 days」假显示）
        const parseExpireMs = raw => {
          if (raw == null || raw === '') return NaN
          const ms = new Date(raw).getTime()

          return Number.isFinite(ms) && ms > 0 ? ms : NaN
        }
        const expireTime = [deviceUser.expire_time, device?.expire_time].map(parseExpireMs).find(Number.isFinite)
        if (device) {
          setDeviceId(device.id)

          if (orders === undefined) {
            const result = await getUserAccountOrder({ page: 1, pageSize: 9999 })
            applyOrderRow(device, result.data)
          }

          // 详情页只复用「续费失败 → 选择支付方式」能力，不重复展示首页到期 Banner。
          if (paymentFallbackOnly) return

          updateExpirationFromServer(expireTime, serverTimeRes?.data?.server_time)
        }
      }
    }
    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceUserKey, applyOrderRow, isDisplayPayment, paymentFallbackOnly, updateExpirationFromServer])

  useEffect(() => {
    const selectedDeviceId = getSelectedDeviceId()
    if (selectedDeviceId) setDeviceId(selectedDeviceId)
    if (!Array.isArray(orders) || orders.length === 0) return
    const id = selectedDeviceId || deviceId
    if (id) applyOrderRow(id, orders)
  }, [deviceId, orders, applyOrderRow, isDisplayPayment])

  const preventScroll = useCallback(e => {
    e.preventDefault()
  }, [])

  const prevBodyOverflowRef = useRef(null)
  const prevBodyOverflowXRef = useRef(null)

  useEffect(() => {
    if (isDisplay2 || isDisplayPayment) {
      // 兼容“只阻止滚动但滚动条仍可见”的情况：直接隐藏 body 滚动条
      if (prevBodyOverflowRef.current === null) {
        prevBodyOverflowRef.current = document.body.style.overflow
        prevBodyOverflowXRef.current = document.body.style.overflowX
      }
      document.body.style.setProperty('overflow', 'hidden', 'important')
      document.body.style.setProperty('overflow-x', 'hidden', 'important')

      document.addEventListener('wheel', preventScroll, { passive: false })
      document.addEventListener('touchmove', preventScroll, { passive: false })
    } else {
      document.removeEventListener('wheel', preventScroll)
      document.removeEventListener('touchmove', preventScroll)

      // 恢复 body 样式
      if (prevBodyOverflowRef.current !== null) {
        document.body.style.overflow = prevBodyOverflowRef.current
        document.body.style.overflowX = prevBodyOverflowXRef.current ?? ''
        prevBodyOverflowRef.current = null
        prevBodyOverflowXRef.current = null
      }
    }

    return () => {
      document.removeEventListener('wheel', preventScroll)
      document.removeEventListener('touchmove', preventScroll)

      if (prevBodyOverflowRef.current !== null) {
        document.body.style.overflow = prevBodyOverflowRef.current
        document.body.style.overflowX = prevBodyOverflowXRef.current ?? ''
        prevBodyOverflowRef.current = null
        prevBodyOverflowXRef.current = null
      }
    }
  }, [isDisplay2, isDisplayPayment, preventScroll])

  const [showAnimation, setShowAnimation] = useState(false)

  useEffect(() => {
    let timer
    if (isDisplayResult) {
      // 触发显示动画
      setShowAnimation(true)

      // 三秒后开始消失动画
      timer = setTimeout(() => {
        setShowAnimation(false) // 先做消失动画
      }, 3000)
    }

    return () => clearTimeout(timer)
  }, [isDisplayResult])

  // 当消失动画结束再隐藏整个 div
  const handleTransitionEnd = () => {
    if (!showAnimation) {
      setIsDisplayResult(false)
    }
  }

  const [currency, setCurrency] = useState('USD') // 当前选择货币
  const [exchangeRate, setExchangeRate] = useState(1) // 美元转换当前货币汇率

  useEffect(() => {
    const initData = async () => {
      try {
        const ip = await countryAndIp()
        const lowerCountry = ip?.country?.toLowerCase() || 'us'
        let currentCurrency = 'USD'

        if (lowerCountry !== 'us') {
          const match = currencyMap.find(item => item.country.toLowerCase() === lowerCountry)
          currentCurrency = match ? match.currency : 'USD'
        }
        setCurrency(currentCurrency)

        if (currentCurrency !== 'USD') {
          fetch(`https://api.frankfurter.app/latest?from=USD&to=${currentCurrency}`)
            .then(res => res.json())
            .then(data => {
              const rate = data.rates?.[currentCurrency]

              if (rate) {
                setExchangeRate(rate)
              } else {
                setExchangeRate(fallbackRates[currentCurrency] || 1)
              }
            })
            .catch(err => {
              console.error(err)
              setExchangeRate(fallbackRates[currentCurrency] || 1)
            })
        } else {
          setExchangeRate(1)
        }
      } catch (error) {
        console.error('初始化国家货币失败：', error)
      }
    }

    if (isDisplayPayment) initData()
  }, [isDisplayPayment])

  const handleRenewalSuccess = useCallback(async () => {
    setIsDisplay(false)
    setIsDisplay2(false)
    setIsDisplayPayment(false)
    setPaymentResult('success')
    setIsDisplayResult(true)

    // grace（过期 ≤30 天）常走本组件：banner 续费 / Guide 失败兜底。
    // refreshSubscriptionExpire 只 bump revision、不动 Redux deviceUser，
    // 过期 banner / 遮罩会继续停在「已过期」视图，必须整页刷新。
    reloadAfterRenewSuccess()

    try {
      const [deviceRes, orderRes, serverTimeRes] = await Promise.all([
        getUserAccountDevice({ page: 1, pageSize: 10 }),
        getUserAccountOrder({ page: 1, pageSize: 9999 }),
        getServerTime().catch(() => null)
      ])
      if (deviceRes?.code !== 0) {
        await refreshSubscriptionExpire({ dispatch })

        return
      }

      const device = resolveCurrentDevice(deviceRes.data)
      if (device) setDeviceId(device.id)
      const row = device
        ? resolveDeviceOrder({
            orders: orderRes?.data,
            deviceUser: readDeviceUser(),
            device,
            deviceId: device.id,
            iCloudUsername: device.iCloud_username
          })
        : null
      const expireTimeRaw = row?.expire_time ?? device?.expire_time

      await refreshSubscriptionExpire({ dispatch, expireTime: expireTimeRaw })
      updateExpirationFromServer(expireTimeRaw, serverTimeRes?.data?.server_time)

      if (row) {
        applyOrderRow(device, orderRes.data)
      }
    } catch (e) {
      // reminders already dismissed; sync is best-effort
      await refreshSubscriptionExpire({ dispatch })
    }
  }, [dispatch, applyOrderRow, updateExpirationFromServer])

  const RenewSubscription = async id => {
    try {
      const renewId = selectedPlan?.productId ?? (await fetchRenewProductIdForOrder(order))
      if (!renewId) {
        setIsDisplayPayment(true)

        return
      }

      const res = await extendSubscriptionTime({
        merchant_no: order.merchant_no,
        renewId,
        order_type: id === 0 ? 272 : 273
      })
      if (res?.code == 0) {
        await handleRenewalSuccess()
      } else {
        setPaymentResult('error')
        setErrorType(res.msg)
        setIsDisplayPayment(true)
      }
    } catch (error) {
      console.error('续订失败：', error)

      // HTTP 500（如当前支付方式不支持续费）会被 Axios 抛到 catch；
      // 与业务失败码保持一致，继续展示新支付方式兜底弹窗。
      setPaymentResult('error')
      setErrorType(error?.response?.data?.msg || error?.msg || t('Payment failed'))
      setIsDisplayPayment(true)
    }
  }

  const handleClick = async () => {
    if (checkID === 0 && !selectedPlan?.productId) return

    if (checkID === 0) {
      try {
        const res = await extendSubscriptionTime({
          merchant_no: order.merchant_no,
          renewId: selectedPlan.productId
        })
        if (res?.code == 0) {
          await handleRenewalSuccess()
        } else {
          setPaymentResult('error')
          setErrorType(res.msg)
          setIsDisplayResult(true)
        }
      } catch (error) {
        console.error('续订失败：', error)
      }
    } else {
      navigateTo(getRenewCheckoutHref())
    }
  }

  useEffect(() => {
    const handler = event => {
      // 标记「失败已由本弹窗接管」：调用方（如 ExpiredPurchaseGuide）会据此
      // 判断无需再跳官网，避免失败时把用户甩到价格页
      event?.preventDefault?.()
      setIsDisplayPayment(true)
    }
    window.addEventListener('renewalFailed', handler)

    return () => {
      window.removeEventListener('renewalFailed', handler)
    }
  }, [])

  const ref = useRef(null)

  // 移动端套餐列表是独立的底部面板（不在 ref 内），需要单独判断
  const mobilePlanRef = useRef(null)
  const bannerRef = useRef(null)
  const [bannerHeight, setBannerHeight] = useState(0)

  // 与 header 同一套下滑判定，用 transform 上移，避免改 sticky top 导致抖动
  const hideOnScroll = useScrollTrigger(HEADER_HIDE_SCROLL_OPTIONS)

  useEffect(() => {
    const handleClickOutside = event => {
      const target = event.target

      // 桌面下拉与移动端底部面板是两套 DOM，移动端面板不在 ref 内。
      // 只判断 ref 时，按下（mousedown）会先把面板收起（display:none），
      // 导致随后的 click 无处派发，套餐选不中。
      if (ref.current?.contains(target) || mobilePlanRef.current?.contains(target)) return
      setIsDisplayPlan(false)
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // fixed 后测量高度做占位
  useLayoutEffect(() => {
    if (!isDisplay) {
      setBannerHeight(0)

      return
    }
    const el = bannerRef.current
    if (!el) return

    const updateHeight = () => setBannerHeight(el.offsetHeight)
    updateHeight()
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateHeight) : null
    ro?.observe(el)

    return () => ro?.disconnect()
  }, [isDisplay, remainingDays, isExpired, locale])

  return (
    <>
      {!paymentFallbackOnly && isDisplay && (
        <>
          {/* fixed 脱离文档流后用占位撑开；负 margin 抵消 ContentWrapper padding，避免顶部双倍空隙 */}
          <div
            className='w-full -mt-3 mb-5 Header5:big:mt-[-60px] bigs:mt-[-90px] smjs:mt-[-40px] smjs:mb-[40px]'
            style={{ height: bannerHeight || 72 }}
            aria-hidden
          />
          {/* 桌面/移动统一 fixed 贴在 header（60px）下方。
              原先桌面用 sticky：滚动容器是 ContentWrapper，再叠加负 margin 后吸顶常失效，
              滚动时 banner 会跟着内容走，看起来「没有固定在上方」。 */}
          <div
            ref={bannerRef}
            className={`fixed top-[60px] left-0 right-0 z-[1099] smjs:left-[300px] w-auto mx-0 mt-0 mb-0 transition-transform duration-[225ms] ease-[cubic-bezier(0,0,0.2,1)] smjs:bg-[url("/image/banner/banner-pc-bg.svg")] smj:bg-[url("/image/banner/banner-phone-bg.svg")] bg-no-repeat bg-center bg-cover flex smj:flex-col justify-center items-center py-[11px] smj:py-2 smjs:px-5 ${
              hideOnScroll ? 'smj:-translate-y-[60px]' : 'translate-y-0'
            }`}
          >
            <div className='flex items-center'>
              <Image
                src='/image/banner/banner.png'
                width={26}
                height={26}
                alt=''
                className='smj:w-[18px] smj:h-[18px] animate-[swingClock_4s_cubic-bezier(0.4,0,0.2,1)_infinite]'
              />
              <p className='ml-4 smj:ml-2 text-[14px] smj:text-[12px] leading-[20px] smj:leading-[18px] font-[500] text-white'>
                {isExpired ? t('Your monitoring plan expired') : t('Your monitoring plan will expire in')}{' '}
                <span className='text-[#FFCF55] font-[600]'>
                  {isExpired ? `${expirationDays} ${t('days ago')}` : `${remainingDays} ${t('days')}.`}
                </span>
                <span className='smj:hidden'>
                  {' '}
                  {isExpired
                    ? t(
                        'and is currently in the grace period Please renew it as soon as possible otherwise the service will be interrupted'
                      )
                    : t('Please renew promptly to avoid service interruption')}
                </span>
              </p>
            </div>
            <button
              className='smjs:ml-[48px] w-auto h-[32px] smj:h-[28px] px-5 bg-[#FFCF55] rounded-[16px] text-[14px] smj:text-[12px] text-[#282938] smj:mt-2 flex-shrink-0'
              onClick={() => {
                RenewSubscription(0)
              }}
            >
              {t('Renew Now')}
            </button>
            <Image
              src={'/image/banner/close.svg'}
              alt=''
              width={20}
              height={20}
              className={`smj:w-4 smj:h-4 absolute right-6 top-[17px] smj:top-2 smj:right-2 cursor-pointer ${
                isExpired && 'hidden'
              }`}
              onClick={() => {
                setIsDisplay(false)
                if (serverDate) localStorage.setItem(`closeExpirationReminder-${username}`, serverDate)
              }}
            />
          </div>
        </>
      )}
      {!paymentFallbackOnly && isDisplay2 && (
        <div className='fixed bottom-0 right-0 top-[60px] z-[1200] bg-[#00000080] smj:left-0 smjs:left-[300px]'>
          <div className='relative mx-auto mt-[280px] smj:mt-[25vh] w-[550px] smj:w-[calc(100vw-32px)] h-auto p-8 smj:pt-10 smj:pb-6 smj:px-6 bg-white rounded-[12px] smjs:translate-x-[-150px]'>
            <Image src={'/image/banner/warn.svg'} alt='' width={48} height={48} className='mx-auto' />
            <p className='mt-3 font-[600] text-[20px] smj:text-[18px] leading-[28px] smj:leading-[26px] text-[#282938] text-center'>
              {isExpiredPopup
                ? t('Your monitoring subscription has expired')
                : t('Your monitoring subscription is about to expire')}
            </p>
            <p className='mt-6 text-[15px] leading-[24px] smj:text-[14px] smj:leading-[22px] text-[#484B60] text-start'>
              {isExpiredPopup
                ? `${t('Your')} MSAFELY ${(order?.product_name?.match(/\d+/) || ['1'])[0]}${t(
                    '-Month Premium Subscription expired on'
                  )} ${order?.expire_time?.slice(0, 10)}. ${t(
                    'New data is no longer being updated Please renew promptly to restore all features All data will be deleted 30 days after expiration'
                  )}`
                : `${t('Your')} MSAFELY ${(order?.product_name?.match(/\d+/) || ['1'])[0]}${t(
                    '-Month Premium Subscription expired on'
                  )} ${order?.expire_time?.slice(0, 10)}. ${t(
                    'New data is no longer being updated Please renew promptly to restore all features All data will be deleted 30 days after expiration'
                  )}`}
            </p>
            <button
              className='mt-6 w-auto h-[42px] px-10 bg-[#FFCF55] rounded-[40px] text-[14px] text-[#282938] mx-auto block font-[600]'
              onClick={() => {
                RenewSubscription(1)
              }}
            >
              {t('Extend Subscription')}
            </button>
            <Image
              src={'/image/banner/close.svg'}
              alt=''
              width={24}
              height={24}
              className='smj:w-5 smj:h-5 absolute top-3 right-3 cursor-pointer'
              onClick={() => {
                setIsDisplay2(false)
              }}
            />
          </div>
        </div>
      )}
      {isDisplayPayment && (
        <div className='fixed bottom-0 right-0 top-[60px] z-[1200] bg-[#00000080] smj:left-0 smjs:left-[300px]'>
          <div className='relative mx-auto mt-[280px] smj:mt-[25vh] w-[550px] smj:w-[calc(100vw-32px)] h-auto p-6 bg-white rounded-[10px] smjs:translate-x-[-150px] shadow-[0px_6px_14px_0px_#28293826]'>
            <p className='font-[600] text-[18px] leading-[26px] smj:text-[16px] smj:leading-[24px] text-[#282938]'>
              {t('Choose your payment method')}:
            </p>
            {order?.bill_card ? (
              <div
                className={`mt-4 mb-3 w-full h-[36px] flex items-center rounded-[6px] cursor-pointer ${
                  checkID === 0 ? 'bg-[#454CFF1A] [box-shadow:0px_0px_0px_1px_#454CFF]' : 'bg-[#F4F5F6]'
                }`}
                onClick={() => {
                  setCheckID(0)
                }}
              >
                <Image
                  src='/extreme/card.webp'
                  alt='card'
                  width={32}
                  height={22}
                  className='!w-[32px] !h-[22px] ml-3 mr-[15px]'
                />
                <p className='text-[#454CFF] text-[14px] font-[500]'>**** **** **** {order.bill_card}</p>
              </div>
            ) : null}
            <div
              className={`w-full h-[36px] flex items-center rounded-[6px] cursor-pointer ${
                checkID === 1 ? 'bg-[#454CFF1A] [box-shadow:0px_0px_0px_1px_#454CFF]' : 'bg-[#F4F5F6]'
              }`}
              onClick={() => {
                // setCheckID(1)
                navigateTo(getRenewCheckoutHref())
              }}
            >
              <p className='text-[#454CFF] text-[13px] ml-3 font-[500]'>+ {t('Add another card or payment method')}</p>
            </div>
            <p className='font-[600] text-[18px] leading-[26px] smj:text-[16px] smj:leading-[24px] text-[#282938] mt-4 mb-3'>
              {t('Order details')}:
            </p>
            <div className='flex justify-between items-center text-[14px] leading-[20px]'>
              <p className='text-[#9198AD]'>{t('Youre getting')}:</p>
              <p className='text-[#2A313B] text-end'>{t('Subscription extension')}</p>
            </div>
            <div className='flex justify-between text-[14px] leading-[20px] my-[10px]'>
              <p className='text-[#9198AD] flex-shrink-0'>{t('You Plan')}:</p>
              <div
                className='flex items-start relative cursor-pointer'
                ref={ref}
                onClick={() => {
                  setIsDisplayPlan(!isDisplayPlan)
                  if (!isDisplayPlan) {
                  }
                }}
              >
                <p className='text-[#2A313B] text-end'>
                  Msafely {selectedPlan?.months ?? '-'}
                  {t('-Month Premium Subscription')}
                </p>
                <Image
                  src={'/image/banner/more-less.svg'}
                  alt=''
                  width={14}
                  height={14}
                  className={`ml-3 smj:ml-[5px] mt-[3px] flex-shrink-0 transition-transform duration-300 ease-in-out ${
                    isDisplayPlan ? 'rotate-180' : 'rotate-0'
                  }`}
                />
                <div
                  className={`smj:hidden absolute z-[1201] top-[28px] right-[-16px] w-[190px] h-auto !rounded-[4px] overflow-hidden bg-white shadow-[0px_0px_8px_0px_#00000026] ${
                    isDisplayPlan || 'hidden'
                  }`}
                >
                  {plans.map((plan, index) => (
                    <div
                      key={plan.productId}
                      className={`p-[6px_9px_6px_12px] flex justify-between items-center ${
                        planIndex === index ? 'bg-[#F5F5F5]' : ''
                      }`}
                      onClick={e => {
                        e.stopPropagation()
                        setPlanIndex(index)
                        setIsDisplayPlan(false)
                      }}
                    >
                      <div>
                        <p className='text-[#222222] text-[14px] leading-[21px]'>{t(plan.periodKey)}</p>
                        <p className='text-[#666666] text-[12px] leading-[18px]'>
                          {currency === 'USD' ? 'US$' : currency}
                          {(Number(plan.monthlyPrice) * exchangeRate).toFixed(2)}/{t('mo')}
                          {plan.showOriginal && plan.monthlyOriginal ? (
                            <s className='ml-1 opacity-50'>
                              {(Number(plan.monthlyOriginal) * exchangeRate).toFixed(2)}
                            </s>
                          ) : null}
                        </p>
                      </div>
                      <Image
                        src={'/image/banner/right.svg'}
                        alt=''
                        width={12}
                        height={12}
                        className={`${planIndex === index ? 'block' : 'hidden'}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div
                className={`fixed inset-0 w-full h-full bg-[#00000066] z-[1201] smjs:hidden ${
                  isDisplayPlan || 'hidden'
                }`}
                onClick={() => setIsDisplayPlan(false)}
              >
                <div
                  ref={mobilePlanRef}
                  className='absolute bottom-0 right-0 left-0 w-full h-auto bg-white'
                  onClick={e => e.stopPropagation()}
                >
                  {plans.map((plan, index) => (
                    <div
                      key={plan.productId}
                      className={`p-[8px_18px_8px_12px] flex justify-between items-center ${
                        planIndex === index ? 'bg-[#F5F5F5]' : 'bg-white'
                      }`}
                      onClick={() => {
                        setPlanIndex(index)
                        setIsDisplayPlan(false)
                      }}
                    >
                      <div>
                        <p className='text-[#222222] text-[14px] leading-[21px]'>{t(plan.periodKey)}</p>
                        <p className='text-[#666666] text-[12px] leading-[18px]'>
                          {currency === 'USD' ? 'US$' : currency}
                          {(Number(plan.monthlyPrice) * exchangeRate).toFixed(2)}/{t('mo')}
                          {plan.showOriginal && plan.monthlyOriginal ? (
                            <s className='ml-1 opacity-50'>
                              {(Number(plan.monthlyOriginal) * exchangeRate).toFixed(2)}
                            </s>
                          ) : null}
                        </p>
                      </div>
                      <Image
                        src={'/image/banner/right.svg'}
                        alt=''
                        width={12}
                        height={12}
                        className={`${planIndex === index ? 'block' : 'hidden'}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className='flex justify-between items-center text-[14px] leading-[20px]'>
              <p className='text-[#9198AD]'>{t('Extension period')}:</p>
              <p className='text-[#2A313B]'>
                {selectedPlan ? t(selectedPlan.periodLabelKey) : '-'}
              </p>
            </div>
            <div className='flex justify-between items-center text-[14px] leading-[20px] mt-[10px]'>
              <p className='text-[#9198AD]'>{t('Monthly price')}:</p>
              <p className='text-[#2A313B]'>
                {currency === 'USD' ? 'US$' : currency}
                {selectedPlan ? (Number(selectedPlan.monthlyPrice) * exchangeRate).toFixed(2) : '-'}
                {selectedPlan?.showOriginal && selectedPlan.monthlyOriginal ? (
                  <s className='ml-2 opacity-50'>
                    {(Number(selectedPlan.monthlyOriginal) * exchangeRate).toFixed(2)}
                  </s>
                ) : null}
              </p>
            </div>
            <div className='flex smj:flex-col justify-between items-center mt-6 font-[600]'>
              <button
                className='w-[240px] smj:w-full h-[42px] border-[1px] border-[#454CFF] rounded-[40px] flex justify-center items-center text-[#454CFF] text-[14px] smj:mt-[17px] smj:order-1'
                onClick={() => {
                  setIsDisplayPayment(false)
                }}
              >
                {t('Cancel')}
              </button>
              <button
                className='w-[240px] smj:w-full h-[42px] bg-[#FFCF55] rounded-[40px] flex justify-center items-center text-[#282938] text-[14px] smj:order-0 disabled:opacity-50 disabled:cursor-not-allowed'
                disabled={checkID === 0 && !selectedPlan?.productId}
                onClick={() => {
                  handleClick()
                }}
              >
                {t('Confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
      {isDisplayResult && (
        <div
          className={`fixed z-[1201] top-[60px] left-1/2 translate-x-[calc(-50%+150px)] smj:translate-x-[-50%] w-[400px] smj:w-[calc(100vw-32px)] h-auto bg-white rounded-[8px] p-3 smj:py-2 shadow-[0px_1px_6px_0px_#28293826] transform transition-all duration-300 ease-in-out ${
            showAnimation ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
          }`}
          onTransitionEnd={handleTransitionEnd}
        >
          <div className='flex'>
            <Image
              src={
                paymentResult === 'success'
                  ? '/image/success.svg'
                  : paymentResult === 'error'
                  ? '/image/error.svg'
                  : paymentResult === 'warn'
                  ? '/image/warn.svg'
                  : paymentResult === 'remind'
                  ? '/image/remind.svg'
                  : ''
              }
              alt=''
              width={24}
              height={24}
              className='w-[24px] h-[24px]'
            />
            <div className='ml-3'>
              <p className='text-[16px] leading-[24px] font-[600] text-[#282938]'>
                {paymentResult === 'success'
                  ? t('Successful')
                  : paymentResult === 'error'
                  ? t('Payment failed')
                  : paymentResult === 'warn'
                  ? t('Insufficient balance')
                  : paymentResult === 'remind'
                  ? t('Duplicate payment')
                  : t('Other')}
              </p>
              <p className='text-[14px] leading-[22px] text-[#1D2129]'>
                {paymentResult === 'success'
                  ? t('You have successfully paid')
                  : paymentResult === 'error'
                  ? errorType
                  : t('No network')}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ExpirationReminder

